import React from 'react';
import { useNavigate } from 'react-router-dom';
import { WizardRecapHeader } from '@gov-nx/component/web';
import { isFileUploadItemWeb } from '@gov-nx/core/hooks';
import { useLocale, useTranslationWithNamespace } from '@gov-nx/core/service';
import { filter, pipe } from '@gov-nx/core/types';
import { useEPeticeEditContext } from '@gov-nx/module/page';
import {
	Button,
	ButtonGroupLayout,
	CommaSpace,
	LayoutGap,
	Space,
} from '@gov-nx/ui/web';
import { phoneShapeToString } from '@gov-nx/utils/common';
import { pageRouteResolver } from '@gov-nx/web';
import { EPetitionPerex } from '../detail/EPetitionPerex';
import { EPetitionText } from '../detail/EPetitionText';

const Title = ({ title, stepIndex }: { title: string; stepIndex: number }) => {
	const { code, wizard } = useEPeticeEditContext();
	const { t } = useLocale(code);

	return (
		<WizardRecapHeader
			title={title}
			wizard={wizard}
			stepIndex={stepIndex}
			strings={{
				label: t('akce.upravit'),
				wcagLabel: `${t('akce.wcag.upravit')}: ${title}`,
			}}
		/>
	);
};

export const WizardStep4 = () => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const {
		code,
		petitionId,
		wizard,
		zakladatelTyp,
		formStep1,
		formStep2,
		isSubmitConfirmModalOpen,
		isDeleteConfirmModalOpen,
		isProcessing,
		controls,
		onSave,
	} = useEPeticeEditContext();
	const tsn = getLocalizeCurried(code);
	const navigate = useNavigate();

	const step2Data = formStep2?.formDefinition.formMethods.getValues();

	return (
		<LayoutGap gap="xl">
			<LayoutGap gap="s">
				<Title
					title={tsn('formular.nadpis.popis-epetice')}
					stepIndex={1}
				/>
				<dl>
					<div>
						<dt className="inline mr-1!">{tsn('formular.popis.nazev')}</dt>
						<dd className="inline">
							{formStep1.formMethods.getValues('nazev')}
						</dd>
					</div>
					<div>
						<dt className="inline mr-1!">{tsn('formular.popis.perex')}</dt>
						<dd className="inline">
							<EPetitionPerex perex={step2Data.perex} />
						</dd>
					</div>
					<div>
						<dt className="inline mr-1!">{tsn('formular.popis.text')}</dt>
						<EPetitionText
							text={step2Data.text}
							toggleSize="s"
							strings={{
								showMore: tsn('formular.popis.zobrazit-cely'),
								showLess: tsn('formular.popis.zobrazit-zkraceny'),
							}}
						/>
					</div>
				</dl>
			</LayoutGap>

			<LayoutGap gap="s">
				<Title
					title={tsn('formular.nadpis.priloha')}
					stepIndex={1}
				/>
				<dl>
					<dt className="inline mr-1! whitespace-nowrap">
						{tsn('formular.popis.priloha')}
					</dt>
					<dd className="inline mr-1!">
						<ol className="count-inline">
							{pipe(
								wizard.formData.priloha,
								filter(isFileUploadItemWeb),
								(attachments) => {
									if (Array.isArray(attachments) && attachments.length > 0) {
										return attachments.map((attachment) => (
											<li key={attachment.id}>
												<Button
													color="primary"
													type="link"
													className="button-text-inline"
													onClick={() =>
														window.open(URL.createObjectURL(attachment.file))
													}
													aria-label="formular.popis.otevrit-prilohu">
													{attachment.name}
												</Button>
											</li>
										));
									}

									return tsn('formular.popis.bez-prilohy');
								}
							)}
						</ol>
					</dd>
				</dl>
			</LayoutGap>

			<LayoutGap gap="s">
				<Title
					title={tsn('formular.nadpis.adresat')}
					stepIndex={1}
				/>
				<dl>
					<dt className="inline mr-1!">{tsn('formular.popis.adresat')}</dt>
					<dd className="inline">
						{wizard.formData.adresatOvm?.selected
							? wizard.formData.adresatOvm.selected.nazev
							: tsn('formular.zprava.adresat-nevybran')}
					</dd>
				</dl>
			</LayoutGap>

			<LayoutGap gap="s">
				<Title
					title={tsn('formular.nadpis.kontakt-zakladatel')}
					stepIndex={1}
				/>
				<dl>
					<div>
						<dt className="inline mr-1!">{tsn('formular.popis.email')}:</dt>
						<dd className="inline">
							{step2Data?._useEmail && step2Data.email
								? step2Data.email
								: tsn('formular.zprava.email-neuveden')}
						</dd>
					</div>
					<div>
						<dt className="inline mr-1!">{tsn('formular.popis.telefon')}:</dt>
						<dd className="inline">
							{step2Data?._usePhone && step2Data.phone
								? pipe(step2Data.phone, (shape) =>
										shape ? phoneShapeToString(shape) : null
									)
								: tsn('formular.zprava.telefon-neuveden')}
						</dd>
					</div>
					{zakladatelTyp === 'FO' ? (
						<div>
							<dt className="inline mr-1!">{tsn('formular.popis.adresa')}:</dt>
							<dd className="inline">
								{pipe(
									formStep2.formDefinition.formMethods.getValues('_useAddress'),
									(useAddress) =>
										useAddress
											? (formStep2.formDefinition.formMethods.getValues(
													'adresa.adresaText'
												) ?? tsn('formular.zprava.adresa-neuvedena'))
											: tsn('formular.zprava.adresa-neuvedena')
								)}
							</dd>
						</div>
					) : null}
				</dl>
			</LayoutGap>

			{zakladatelTyp === 'FO' ? (
				<LayoutGap gap="s">
					<Title
						title={tsn('formular.nadpis.peticni-vybor')}
						stepIndex={1}
					/>
					<dl>
						<dt className="whitespace-nowrap">
							{tsn('formular.popis.peticni-vybor')}
						</dt>
						<dd>
							<ol className="list-decimal">
								{pipe(
									formStep2?.formDefinition.formMethods.getValues(
										'clenovePeticnihoVyboru'
									),
									(members) => {
										if (Array.isArray(members) && members.length > 0) {
											return members.map((member) => (
												<li
													key={[
														member.id,
														member.jmeno,
														member.prijmeni,
														member.adresa?.adresaText,
													].join('-')}>
													{member.jmeno}
													<Space />
													{member.prijmeni}
													<CommaSpace />
													{member.adresa?.adresaText}
												</li>
											));
										}
										return tsn('formular.popis.peticni-vybor-neuvedeno');
									}
								)}
							</ol>
						</dd>
					</dl>
				</LayoutGap>
			) : null}

			<ButtonGroupLayout>
				<Button
					onClick={isDeleteConfirmModalOpen.setTrue}
					aria-label={tsn('akce.wcag.smazat')}
					isDisabled={isProcessing}
					color="error"
					size="l"
					type="outlined"
					isExpandedMobile>
					{tsn('formular.tlacitko.smazat')}
				</Button>
				<Button
					onClick={() =>
						onSave(() => {
							navigate(pageRouteResolver('epetice.detail', { petitionId }));
						})
					}
					isLoading={controls.saveLoading}
					aria-label={tsn('akce.wcag.ulozit')}
					isDisabled={isProcessing}
					color="primary"
					size="l"
					type="outlined"
					isExpandedMobile>
					{tsn('formular.tlacitko.ulozit')}
				</Button>
				<Button
					onClick={isSubmitConfirmModalOpen.setTrue}
					aria-label={tsn('akce.wcag.odeslat')}
					isDisabled={isProcessing}
					color="primary"
					size="l"
					type="solid"
					isExpandedMobile>
					{tsn('formular.tlacitko.odeslat')}
				</Button>
			</ButtonGroupLayout>
		</LayoutGap>
	);
};
